import { NoImplementationAvailableError } from 'errors';

/**
 * Simple wrapper for window.speechSythesis, in case an alternative voice provider becomes an option.
 *
 * @class RsSpeechAPI
 */
export default class RsSpeechAPI {
  #utterance = null;
  voicesList = null;

  constructor({ getVolume, getVoice, getRate, onVoicesChanged }) {
    const supportsSpeechSynthesis =
      window.speechSynthesis && // supports speech synthesis
      window.SpeechSynthesisUtterance && // supports speech synthesis utterance
      typeof window.speechSynthesis.getVoices === 'function' && // supports getting voices
      typeof window.speechSynthesis.cancel === 'function'; // supports canceling speech synthesis

    // If browser native speech synthesis not available, throw error.
    if (!supportsSpeechSynthesis) {
      throw new NoImplementationAvailableError('speech API');
    }

    // This wrapper class is the perfect opportunity to correct the lack of camel casing in the native api.
    speechSynthesis.onvoiceschanged = onVoicesChanged;
    this.getVoices = speechSynthesis.getVoices.bind(speechSynthesis);

    this.cancel = () => {
      if (this.#utterance) {
        // For whatever reason, speechSynthesis.cancel() doesn't necessarily block the in-flight utterance from running onboundary and onend,
        // at least when testing in Chrome, leading to re-reading chunks of text especially when tracking. This ensures it is fully cleaned up.
        this.#utterance.onboundary = null;
        this.#utterance.onend = null;
        this.#utterance.onerror = null;
      }

      speechSynthesis.cancel(...arguments);
    };

    this.speak = function ({ text, onBoundary, onEnd, onError }) {
      this.#utterance = new window.SpeechSynthesisUtterance(text);
      this.#utterance.volume = getVolume();
      this.#utterance.rate = getRate();
      this.#utterance.onend = onEnd;

      // Handle speech synthesis errors
      this.#utterance.onerror = (event) => {
        // 'interrupted' and 'canceled' are often expected when we call cancel()
        if (event.error === 'interrupted' || event.error === 'canceled') {
          return;
        }

        console.warn('Speech synthesis error:', event.error);
        if (onError) {
          onError(event);
        }
        // If there's an error, we should still call onEnd to continue the TTS flow
        // unless it's a network error which we handle specifically in the provider
        if (onEnd && event.error !== 'network') {
          onEnd();
        }
      };

      // If onboundary is supported, set it to the custom onBoundary.
      if ('onboundary' in this.#utterance) {
        this.#utterance.onboundary = onBoundary;
      }
      // set voice for utterance
      const voicesList = this.voicesList?.length
        ? this.voicesList
        : speechSynthesis.getVoices();
      const selectedVoice = getVoice();

      if (selectedVoice) {
        this.#utterance.voice = voicesList.find((voice) => {
          return voice.voiceURI === selectedVoice;
        });
      } else {
        this.#utterance.voice = voicesList[0];
      }

      speechSynthesis.speak(this.#utterance);
    };
  }

  get onVoicesChanged() {
    return speechSynthesis.onvoiceschanged;
  }

  set onVoicesChanged(onVoicesChanged) {
    speechSynthesis.onvoiceschanged = onVoicesChanged;
  }
}
